// Ported from specter-1 ui/packages/app/src/screens/Research.tsx:247-302.
// One locked change: BLOCK_SELECTOR is now generic `[data-block-id]` instead of
// the specter-specific class list (".research-p, .section-title, ..."). Every
// block in doc.json gets `data-block-id`; the observer attaches generically.

import { useEffect, useState } from "react";
import { termsForText } from "./termPatterns";

const BLOCK_SELECTOR = "[data-block-id]";

export function useActiveGlossaryTerms(
  scrollRef: React.RefObject<HTMLElement | null>,
  mainRef: React.RefObject<HTMLElement | null>,
  allTerms: ReadonlyArray<string>,
): ReadonlySet<string> | null {
  const [active, setActive] = useState<ReadonlySet<string> | null>(null);

  useEffect(() => {
    const root = scrollRef.current;
    const main = mainRef.current;
    if (!root || !main) return;

    const blockTerms = new Map<Element, Set<string>>();
    const intersecting = new Set<Element>();

    const blocks = Array.from(main.querySelectorAll(BLOCK_SELECTOR));
    for (const el of blocks) {
      blockTerms.set(el, termsForText(allTerms, el.textContent ?? ""));
    }

    let raf = 0;
    const recompute = () => {
      raf = 0;
      const union = new Set<string>();
      for (const el of intersecting) {
        const set = blockTerms.get(el);
        if (!set) continue;
        for (const term of set) union.add(term);
      }
      setActive(union);
    };
    const schedule = () => {
      if (raf !== 0) return;
      raf = requestAnimationFrame(recompute);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target);
          else intersecting.delete(entry.target);
        }
        schedule();
      },
      { root, threshold: 0 },
    );
    for (const el of blocks) observer.observe(el);

    return () => {
      observer.disconnect();
      if (raf !== 0) cancelAnimationFrame(raf);
    };
  }, [scrollRef, mainRef, allTerms]);

  return active;
}
